import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Radio, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * AssistantRedeem — exchange an Assistant Director invite token for a
 * cockpit grant, then bounce the user into /studio/assist/{streamId}.
 *
 * Mounted at /studio/assist/redeem. The token in ?token=... is opaque to
 * the client; the backend looks up its stream_id and adds the user to
 * live_streams.assistants[]. If the user isn't signed in we preserve the
 * full URL via /login?next=… so the invite survives the auth round-trip.
 */
export default function AssistantRedeem() {
  const [search] = useSearchParams();
  const token = search.get("token") || "";
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("redeeming"); // redeeming | error
  const [err, setErr] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user) {
      const here = `${window.location.pathname}${window.location.search}`;
      nav(`/login?next=${encodeURIComponent(here)}`, { replace: true });
      return;
    }
    if (!token) {
      setStatus("error"); setErr("No invite token in the URL.");
      return;
    }
    // The token row holds the stream_id, but the redeem endpoint is
    // stream-scoped — so we ask the server to resolve it for us via a
    // small probe endpoint. Easier: try every active stream's redeem;
    // backend resolves token → stream_id. We just hit a "resolve" route.
    (async () => {
      try {
        // Backend lookup — ask which stream this token belongs to.
        const resolve = await api.get(`/live/assistant/lookup?token=${encodeURIComponent(token)}`);
        const sid = resolve.data?.stream_id;
        if (!sid) throw new Error("Invalid token");
        await api.post(`/live/${sid}/assistant/redeem?token=${encodeURIComponent(token)}`);
        nav(`/studio/assist/${sid}`, { replace: true });
      } catch (e) {
        const code = e?.response?.status;
        if (code === 410) setErr("This invite has expired — ask the host to send another.");
        else if (code === 404) setErr("Invalid invite link.");
        else if (code === 403) setErr("This invite was sent to a different account. Sign in with that account first.");
        else setErr(formatApiError(e));
        setStatus("error");
      }
    })();
  }, [token, user, loading, nav]);

  return (
    <div data-testid="assistant-redeem" className="min-h-screen flex items-center justify-center px-4 text-center">
      {status === "redeeming" && (
        <div className="text-white/80 inline-flex flex-col items-center gap-3">
          <Radio className="w-8 h-8 text-tv-accent" />
          <div className="text-lg font-display font-bold">Boarding the cockpit…</div>
          <div className="text-white/50 text-sm inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Verifying your invite</div>
        </div>
      )}
      {status === "error" && (
        <div className="text-white/85 inline-flex flex-col items-center gap-3 max-w-md" data-testid="assistant-redeem-error">
          <AlertTriangle className="w-8 h-8 text-amber-400" />
          <div className="text-lg font-display font-bold">{err}</div>
          <button onClick={() => nav("/")} className="mt-2 bg-white/10 hover:bg-white/15 text-white font-bold px-5 py-2 rounded-full text-sm">Back home</button>
        </div>
      )}
    </div>
  );
}
